import React, {useCallback, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useNavigate} from 'react-router-dom';
import notificationStore, {NotificationItem, NotificationPage} from '../store/NotificationStore';
import NotificationItemRow from '../components/NotificationItemRow';
import Spinner from '../components/Spinner';
import {BellIcon, CheckCheckIcon} from '../components/icons';
import {resolveDeepLink} from '../support/deepLink';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import PageSizeSelect from '../components/PageSizeSelect';
import {usePageSize} from '../hooks/usePageSize';

/**
 * Страница «Все уведомления» (CTHS-43): полный список с пагинацией поверх GET /api/notifications.
 * Состояния loading/empty/error в data region; mark-read по клику и «прочитать все». Пагинированный
 * список держится в локальном стейте (badge/выпадашка — в {@link notificationStore}).
 */
const NotificationsPage: React.FC = observer(() => {
    const [page, setPage] = useState<NotificationPage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const navigate = useNavigate();
    // Размер страницы истории уведомлений выбирает пользователь и он сохраняется (CTHS-7).
    const [pageSize, setPageSize] = usePageSize('notifications');

    const load = useCallback(async (n: number, size: number): Promise<void> => {
        setLoading(true);
        setError(false);
        try {
            const p = await notificationStore.fetchPage(n, size);
            setPage(p);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    // Смена размера страницы возвращает к началу истории: номер прежней страницы после
    // пересчёта означает уже другое место, а то и несуществующее (FR-4.5).
    const changePageSize = (size: number): void => {
        setPageSize(size);
        load(0, size);
    };

    useDocumentTitle();

    useEffect(() => {
        load(0, pageSize);
        notificationStore.loadUnreadCount();
        // Первая загрузка: размер берётся из сохранённого выбора, дальше им управляет
        // changePageSize — перезапрос по каждому изменению состояния здесь не нужен.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load]);

    const activate = async (item: NotificationItem): Promise<void> => {
        await notificationStore.markRead(item);
        setPage(prev => prev
            ? {...prev, content: prev.content.map(n => (n.id === item.id ? {...n, read: true} : n))}
            : prev);
        const target = resolveDeepLink(item.deepLink);
        if (target) {
            navigate(target);
        }
    };

    const handleMarkAll = async (): Promise<void> => {
        await notificationStore.markAllRead();
        setPage(prev => prev
            ? {...prev, content: prev.content.map(n => ({...n, read: true}))}
            : prev);
    };

    const pageNumber = page?.number ?? 0;
    const totalPages = page?.totalPages ?? 0;

    return (
        <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Уведомления</h1>
                <button
                    type="button"
                    onClick={handleMarkAll}
                    disabled={notificationStore.unreadCount === 0}
                    data-testid="page-mark-all-read"
                    className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[40px] rounded-lg text-sm text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    <CheckCheckIcon className="w-4 h-4"/>
                    Прочитать все
                </button>
            </div>

            <div className="rounded-2xl border border-gray-100 dark:border-white/10 bg-white dark:bg-ink-800 overflow-hidden shadow-card">
                {loading ? (
                    <div
                        data-testid="page-loading"
                        className="flex items-center justify-center py-16"
                        role="status"
                        aria-live="polite"
                    >
                        <Spinner/>
                    </div>
                ) : error ? (
                    <div data-testid="page-error" className="px-4 py-16 text-center" role="alert">
                        <p className="text-sm text-gray-600 dark:text-gray-300">Не удалось загрузить уведомления.</p>
                        <button
                            type="button"
                            onClick={() => load(pageNumber, pageSize)}
                            className="mt-3 inline-flex px-4 py-2 min-h-[40px] rounded-lg text-sm font-medium text-white bg-ink-900 hover:bg-ink-800 dark:bg-white/10 dark:hover:bg-white/20 transition-colors"
                        >
                            Повторить
                        </button>
                    </div>
                ) : !page || page.content.length === 0 ? (
                    <div data-testid="page-empty" className="px-4 py-16 text-center">
                        <BellIcon className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600"/>
                        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Уведомлений пока нет</p>
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-100 dark:divide-white/5">
                        {page.content.map(item => (
                            <li key={item.id}>
                                <NotificationItemRow item={item} onActivate={activate}/>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {!loading && !error && page && totalPages > 1 && (
                <div className="flex items-center justify-between gap-3 flex-wrap mt-4">
                    <button
                        type="button"
                        onClick={() => load(pageNumber - 1, pageSize)}
                        disabled={page.first}
                        data-testid="page-prev"
                        className="px-4 py-2 min-h-[40px] rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        Назад
                    </button>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                        Страница {pageNumber + 1} из {totalPages}
                    </span>
                    <button
                        type="button"
                        onClick={() => load(pageNumber + 1, pageSize)}
                        disabled={page.last}
                        data-testid="page-next"
                        className="px-4 py-2 min-h-[40px] rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        Вперёд
                    </button>
                </div>
            )}

            {!loading && !error && page && (
                <div className="flex justify-end mt-3">
                    <PageSizeSelect
                        table="notifications"
                        label="Уведомлений на странице"
                        value={pageSize}
                        onChange={changePageSize}
                        totalElements={page.totalElements}
                    />
                </div>
            )}
        </div>
    );
});

export default NotificationsPage;
